(() => {
  const cartMutation = (task) => {
    const header = document.querySelector('sorella-custom-header');
    return header?.runCartMutation ? header.runCartMutation(task) : task();
  };
  const root = () => window.Shopify?.routes?.root || '/';
  const money = (cents, currency = 'EGP') => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency', currency, maximumFractionDigits: 0
  }).format(Number(cents || 0) / 100);
  const groupId = (prefix) => `${prefix}-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;
  const escapeHtml = (value) => {
    const element = document.createElement('span');
    element.textContent = String(value ?? '');
    return element.innerHTML;
  };
  const cartErrorMessage = async (response, fallback = 'This item could not be added.') => {
    const payload = await response.json().catch(() => ({}));
    const raw = payload.description || payload.message
      || (typeof payload.errors === 'string' ? payload.errors : '')
      || Object.values(payload.errors || {}).flat().filter(Boolean).join(' ')
      || fallback;
    const message = String(raw).replace(/^Error:\s*/i, '').trim();
    if (/sold\s*out|out\s*of\s*stock|not\s*enough\s*in\s*stock|unavailable|cannot\s*be\s*sold/i.test(message)) {
      return 'This size is sold out or no longer available. Please choose another size.';
    }
    return message || fallback;
  };

  const groupedExperienceCounts = (cart) => {
    const groups = new Map();
    for (const item of cart.items || []) {
      const experience = item.properties?._sorella_experience;
      const id = item.properties?._sorella_group_id;
      if (!experience || !id) continue;
      const key = `${experience}:${id}`;
      const current = groups.get(key) || { experience, id, count: 0, products: new Set(), native: true };
      current.count += Number(item.quantity || 0);
      current.products.add(String(item.properties?._sorella_source_handle || item.variant_id || item.product_id));
      current.native = current.native && (
        item.properties?._sorella_native_inventory === 'true'
        || item.properties?._sorella_native_bundle === 'true'
      );
      groups.set(key, current);
    }
    return [...groups.values()];
  };

  // These codes are legacy fallback pricing for non-native groups. Native
  // Discovery groups use the real tracked 5ml variants and Shopify's native
  // offer. The reward layer separately blocks all Discovery/bundle edits.
  const managedOfferCodes = new Set(['SORELLA-DISCOVERY6', 'SORELLA-BUNDLE2', 'SORELLA-BUNDLE3']);

  const offerCodesForCart = (cart) => {
    const groups = groupedExperienceCounts(cart);
    const codes = [];
    if (groups.some((group) => !group.native && group.experience === 'discovery' && group.count === 6 && group.products.size === 6)) {
      codes.push('SORELLA-DISCOVERY6');
    }
    const validBundles = groups.filter((group) => !group.native && group.experience === 'bundle'
      && group.count === group.products.size && [2, 3].includes(group.products.size));
    if (validBundles.some((group) => group.products.size === 3)) codes.push('SORELLA-BUNDLE3');
    else if (validBundles.some((group) => group.products.size === 2)) codes.push('SORELLA-BUNDLE2');
    return codes;
  };

  async function cartJson() {
    const response = await fetch(`${root()}cart.js?_sorella=${Date.now()}`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('The cart could not be refreshed.');
    return response.json();
  }

  async function reconcileDiscounts(cart = null) {
    let current = cart || await cartJson();
    const currentCodes = (current.discount_codes || []).map((discount) => discount.code).filter(Boolean);
    const preservedCodes = currentCodes.filter((code) => !managedOfferCodes.has(code));
    const desiredCodes = [...new Set([...preservedCodes, ...offerCodesForCart(current)])];
    if (desiredCodes.join(',') !== currentCodes.join(',')) {
      const response = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: desiredCodes.join(',') })
      });
      if (!response.ok) throw new Error('Your cart offers could not be refreshed.');
      current = await response.json();
    }
    document.dispatchEvent(new CustomEvent('sorella:offers:reconciled', { detail: { cart: current, automatic: true } }));
    return current;
  }

  async function removeExperienceGroup(id) {
    const current = await cartJson();
    const items = current.items.filter((item) => item.properties?._sorella_group_id === id);
    if (!items.length) return current;
    const updates = Object.fromEntries(items.map((item) => [item.key, 0]));
    const response = await fetch(`${root()}cart/update.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates })
    });
    if (!response.ok) throw new Error('The incomplete edit could not be removed from your bag.');
    return response.json();
  }

  function revealCartDrawer() {
    const header = document.querySelector('sorella-custom-header');
    if (header?.openDrawer) header.openDrawer('cart');
    else document.querySelector('[data-open-cart]')?.click();
    return header;
  }

  async function openServerCart(cart = null) {
    const header = revealCartDrawer();
    document.dispatchEvent(new CustomEvent('sorella:cart:updated', { detail: { cart } }));
    if (header?.loadCart) {
      try { await header.loadCart(); } catch (_) { /* The normal cart trigger can still refresh it. */ }
    }
    return cart;
  }

  window.SorellaExperiences = { reconcileDiscounts, groupedExperienceCounts, offerCodesForCart, revealCartDrawer, openServerCart };

  class SorellaExperience extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.experience = this.dataset.sorellaExperience;
      this.nativeInventory = this.dataset.nativeInventory === 'true';
      this.status = this.querySelector('[data-experience-status]');
      if (this.experience === 'mystery') this.setupMystery();
      else this.setupBuilder();
    }

    setStatus(message = '', error = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('is-error', error);
    }

    setupMystery() {
      try { this.mysteryVariants = JSON.parse(this.querySelector('[data-mystery-variants]')?.textContent || '[]'); } catch (_) { this.mysteryVariants = []; }
      try { this.mysteryImages = JSON.parse(this.querySelector('[data-mystery-images]')?.textContent || '{}'); } catch (_) { this.mysteryImages = {}; }
      this.mysteryMain = this.querySelector('[data-mystery-main]');
      this.querySelectorAll('input[name^="mystery-"]').forEach((input) => input.addEventListener('change', () => this.updateMystery()));
      this.querySelectorAll('[data-gallery-image]').forEach((button) => button.addEventListener('click', () => {
        this.querySelectorAll('[data-gallery-image]').forEach((item) => item.removeAttribute('aria-current'));
        button.setAttribute('aria-current', 'true');
        if (this.mysteryMain) this.mysteryMain.src = button.dataset.galleryImage;
      }));
      const dialog = this.querySelector('[data-mystery-info]');
      this.querySelector('[data-open-mystery-info]')?.addEventListener('click', () => dialog?.showModal?.());
      this.querySelectorAll('[data-close-experience-dialog]').forEach((button) => button.addEventListener('click', () => button.closest('dialog')?.close()));
      dialog?.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
      this.querySelector('[data-add-mystery]')?.addEventListener('click', (event) => this.addMystery(event.currentTarget));
      this.updateMystery();
    }

    selectedMystery() {
      const tier = this.querySelector('input[name^="mystery-tier-"]:checked')?.value || '600';
      const mood = this.querySelector('input[name^="mystery-mood-"]:checked')?.value || 'Floral';
      const normalizedTier = tier.replace(/[^0-9]/g, '');
      const variant = this.mysteryVariants.find((item) => {
        const title = String(item.title || '').toLowerCase();
        return title.includes(normalizedTier) && title.includes(mood.toLowerCase());
      });
      return { tier, mood, variant };
    }

    updateMystery() {
      const { tier, mood, variant } = this.selectedMystery();
      const summary = this.querySelector('[data-mystery-summary]');
      const button = this.querySelector('[data-add-mystery]');
      if (summary) summary.textContent = `${tier === '600' ? 'Little' : 'Big'} Mystery · ${mood}`;
      if (button && this.mysteryVariants.length) {
        button.disabled = !variant?.available;
        button.querySelector('span').textContent = variant?.available ? `Add mystery to bag · ${money(variant.price)}` : `${mood} mystery is sold out`;
      }
      const moodImage = this.mysteryImages[mood.toLowerCase()];
      if (moodImage && this.mysteryMain) {
        this.mysteryMain.src = moodImage;
        this.querySelectorAll('[data-gallery-image]').forEach((item) => item.removeAttribute('aria-current'));
      }
      this.setStatus();
    }

    async addMystery(button) {
      const { tier, mood, variant } = this.selectedMystery();
      if (!variant?.available || button.disabled) return;
      const label = button.querySelector('span');
      const original = label.textContent;
      button.disabled = true;
      label.textContent = 'Adding your mystery…';
      this.setStatus();
      try {
        const response = await cartMutation(() => fetch(`${root()}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variant.id), quantity: 1, properties: {
            _sorella_experience: 'mystery', _sorella_offer_tier: tier, 'Scent direction': mood
          } }] })
        }));
        if (!response.ok) throw new Error(await cartErrorMessage(response, 'This mystery could not be added.'));
        await openServerCart();
      } catch (error) {
        this.setStatus(error.message, true);
      } finally {
        button.disabled = false;
        label.textContent = original;
      }
    }

    setupBuilder() {
      this.panel = this.querySelector('[data-builder-panel]');
      this.limit = Number(this.panel?.dataset.limit || 0);
      this.minimum = Number(this.panel?.dataset.min || 0);
      this.choices = [...this.querySelectorAll('[data-builder-choice]')];
      this.selected = new Set();
      this.panel?.querySelectorAll('[data-toggle-choice]').forEach((button) => button.addEventListener('click', () => this.toggleChoice(button.closest('[data-builder-choice]'))));
      this.panel?.querySelectorAll('[data-builder-size]').forEach((button) => button.addEventListener('click', () => this.selectSize(button)));
      this.panel?.querySelector('[data-builder-search]')?.addEventListener('input', () => this.filterChoices());
      this.panel?.querySelector('[data-builder-family]')?.addEventListener('change', () => this.filterChoices());
      this.panel?.querySelector('[data-add-builder]')?.addEventListener('click', (event) => this.addBuilder(event.currentTarget));
      this.updateBuilder();
    }

    toggleChoice(choice) {
      if (!choice || choice.hasAttribute('data-unavailable')) return;
      const productId = choice.dataset.productId;
      if (this.selected.has(productId)) this.selected.delete(productId);
      else if (this.selected.size < this.limit) this.selected.add(productId);
      else {
        this.setStatus(`Your edit is full. Remove one scent before choosing another.`, true);
        choice.animate?.([{ transform: 'translateX(-4px)' }, { transform: 'translateX(4px)' }, { transform: 'none' }], { duration: 220 });
      }
      this.updateBuilder();
    }

    selectSize(button) {
      const choice = button.closest('[data-builder-choice]');
      choice.querySelectorAll('[data-builder-size]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      choice.dataset.variantId = button.dataset.variantId;
      choice.dataset.price = button.dataset.price;
      choice.dataset.image = button.dataset.image;
      choice.dataset.sourceSku = button.dataset.sourceSku || choice.dataset.sourceSku || '';
      choice.dataset.sourceSize = button.dataset.sourceSize || choice.dataset.sourceSize || '';
      const image = choice.querySelector('[data-builder-card-image]');
      if (image && button.dataset.image) { image.src = button.dataset.image; image.srcset = ''; }
      this.updateBuilder();
    }

    chosenChoices() { return this.choices.filter((choice) => this.selected.has(choice.dataset.productId)); }

    updateBuilder() {
      const count = this.selected.size;
      this.choices.forEach((choice) => {
        const active = this.selected.has(choice.dataset.productId);
        choice.classList.toggle('is-selected', active);
        const toggle = choice.querySelector('[data-toggle-choice]');
        toggle?.setAttribute('aria-pressed', String(active));
        const label = toggle?.querySelector('em,span:last-child');
        if (label && !choice.hasAttribute('data-unavailable')) label.textContent = active ? 'Chosen' : (this.experience === 'bundle' ? 'Add to my edit' : 'Choose');
      });
      const countLabel = this.panel?.querySelector('[data-builder-count]');
      if (countLabel) countLabel.textContent = `${count} / ${this.limit} chosen`;
      const progress = this.panel?.querySelector('[data-builder-progress]');
      if (progress) progress.style.width = `${Math.min(100, (count / this.limit) * 100)}%`;
      const add = this.panel?.querySelector('[data-add-builder]');
      const valid = this.experience === 'discovery' ? count === this.limit : count >= this.minimum && count <= this.limit;
      if (add) {
        add.disabled = !valid;
        const label = add.querySelector('span');
        if (label) label.textContent = valid ? (this.experience === 'discovery' ? 'Add my discovery set · EGP 425' : `Add my ${count}-scent bundle`) : `Choose ${Math.max(0, this.minimum - count)} more scent${this.minimum - count === 1 ? '' : 's'}`;
      }
      const help = this.panel?.querySelector('[data-builder-help]');
      if (help && this.experience === 'discovery') help.textContent = valid ? 'Your six-scent edit is ready.' : `Choose ${this.limit - count} more different scent${this.limit - count === 1 ? '' : 's'}.`;
      if (this.experience === 'bundle') this.updateBundleSummary();
    }

    updateBundleSummary() {
      const choices = this.chosenChoices();
      const count = choices.length;
      const heroImage = this.querySelector('[data-bundle-hero-image]');
      const heroSource = count >= 3 ? heroImage?.dataset.threeImage : heroImage?.dataset.twoImage;
      if (heroImage && heroSource && heroImage.src !== new URL(heroSource, window.location.href).href) {
        heroImage.src = heroSource;
        heroImage.srcset = '';
        heroImage.alt = count >= 3 ? 'Three Sorella fragrances for a custom bundle' : 'Two Sorella fragrances for a custom bundle';
      }
      const subtotal = choices.reduce((sum, choice) => sum + Number(choice.dataset.price || 0), 0);
      const discountRate = count === 3 ? 0.15 : count === 2 ? 0.10 : 0;
      const discountedTotal = Math.round(subtotal * (1 - discountRate));
      const totalElement = this.panel.querySelector('[data-bundle-total]');
      if (totalElement) totalElement.textContent = count >= 2 ? money(discountedTotal) : 'Choose two scents';
      const saving = this.panel.querySelector('[data-bundle-saving]');
      if (saving) saving.textContent = count < 2
        ? 'Choose 2 or 3 full-size scents.'
        : `${Math.round(discountRate * 100)}% bundle discount · you save ${money(subtotal - discountedTotal)}`;
      const slots = [...(this.panel.querySelectorAll('[data-builder-slots] i') || [])];
      slots.forEach((slot, index) => {
        const choice = choices[index];
        slot.classList.toggle('is-filled', Boolean(choice));
        slot.innerHTML = choice ? `<img src="${escapeHtml(choice.dataset.image)}" alt=""><span>${escapeHtml(choice.dataset.title)}</span>` : String(index + 1);
      });
    }

    filterChoices() {
      const query = String(this.panel?.querySelector('[data-builder-search]')?.value || '').trim().toLowerCase();
      const family = String(this.panel?.querySelector('[data-builder-family]')?.value || '').trim().toLowerCase();
      this.choices.forEach((choice) => {
        const matchesQuery = !query || `${choice.dataset.title} ${choice.dataset.family}`.toLowerCase().includes(query);
        const matchesFamily = !family || choice.dataset.family === family;
        choice.hidden = !(matchesQuery && matchesFamily);
      });
    }

    async addBuilder(button) {
      const choices = this.chosenChoices();
      const valid = this.experience === 'discovery' ? choices.length === 6 : choices.length >= 2 && choices.length <= 3;
      if (!valid || button.disabled) return;
      const id = groupId(this.experience);
      const tier = this.experience === 'discovery' ? '6x5ml' : `${choices.length}-full-size`;
      const items = choices.map((choice) => {
        const properties = {
          _sorella_experience: this.experience,
          _sorella_group_id: id,
          _sorella_offer_tier: tier,
          _sorella_source_handle: choice.dataset.sourceHandle || choice.dataset.productId,
          _sorella_source_sku: choice.dataset.sourceSku || '',
          'Scent': choice.dataset.title,
          'Size': choice.dataset.sourceSize || (this.experience === 'discovery' ? '5ml' : ''),
          'Sorella experience': this.experience === 'discovery' ? 'Discovery Set' : `${choices.length}-scent bundle`
        };
        if (this.experience === 'bundle' || this.experience === 'discovery') properties._sorella_rewards_excluded = 'true';
        if (this.nativeInventory) properties._sorella_native_inventory = 'true';
        return { id: Number(choice.dataset.variantId), quantity: 1, properties };
      });
      const label = button.querySelector('span');
      const original = label?.textContent;
      button.disabled = true;
      if (label) label.textContent = 'Building your edit…';
      this.setStatus();
      try {
        await cartMutation(async () => {
        const response = await fetch(`${root()}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items })
        });
        if (!response.ok) throw new Error(await cartErrorMessage(response, 'Your edit could not be added.'));
        // Reveal the real cart immediately after Shopify accepts the lines.
        // Offer reconciliation and cart rendering may continue behind it.
        revealCartDrawer();
        let current = await cartJson().catch(() => null);
        try {
          current = await reconcileDiscounts(current || undefined);
        } catch (offerError) {
          // A delayed discount response must never erase a successfully added
          // edit. Shopify will evaluate the same offer again at checkout.
          this.setStatus(this.experience === 'discovery'
            ? 'Your edit was added. Its offer will be confirmed in your bag.'
            : 'Your bundle was added. Its bundle discount will be confirmed in your bag.', false);
        }
        await openServerCart(current);
        });
      } catch (error) {
        this.setStatus(error.message, true);
      } finally {
        button.disabled = false;
        if (label) label.textContent = original;
        this.updateBuilder();
      }
    }
  }

  if (!customElements.get('sorella-experience')) customElements.define('sorella-experience', SorellaExperience);
})();
